import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { ARTICLES, CATEGORIES } from "@/lib/articles";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

/**
 * Compact blog teaser shown on the home page to surface SEO content
 * and create internal links from the home to each article.
 */
export const BlogPreview = () => {
  const items = ARTICLES.slice(0, 3);
  return (
    <section
      id="blog"
      data-testid="blog-preview-root"
      className="relative py-28 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70">
              Journal drones3x
            </div>
            <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
              Tout savoir sur le <span className="italic font-semibold text-[#007AFF]">spectacle de drones</span>.
            </h2>
          </div>
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white">
            Voir tous les articles <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {items.map((a, idx) => (
            <motion.div
              key={a.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
            >
              <Link
                to={`/blog/${a.slug}`}
                data-testid={`blog-preview-card-${a.slug}`}
                className="group block h-full rounded-3xl overflow-hidden glass-strong"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={a.cover} alt={a.title} className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition duration-700" />
                  <span
                    className="absolute top-4 left-4 inline-flex rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
                    style={{ background: CATEGORIES[a.category].color, color: "#020205" }}
                  >
                    {CATEGORIES[a.category].label}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-white/50 mb-3">
                    <span>{formatDate(a.publishedAt)}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={11} /> {a.readingMinutes} min</span>
                  </div>
                  <h3 className="font-display text-lg md:text-xl font-semibold tracking-tight group-hover:text-[#007AFF] transition leading-snug">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed line-clamp-2">
                    {a.excerpt}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-[#007AFF]">
                    Lire l'article <ArrowRight size={12} className="group-hover:translate-x-1 transition" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
